import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../context/UserContext'
import { 
  User, Mail, Phone, MapPin, Calendar, Edit2, Save, X, 
  CheckCircle2, AlertCircle, Building2, Globe, CreditCard,
  Star, BookOpen, Clock
} from 'lucide-react'
import { API_CONFIG } from '../config/settings'

interface ProfileData {
  id: number
  first_name: string | null
  last_name: string | null
  full_name: string | null
  image: string | null
  username: string | null
  email: string
  email_verified: boolean
  email_verified_at: string | null
  contact_number: string | null
  address: string | null
  city: string | null
  state: string | null
  country: string | null
  status: number
  created_at: string | null
  updated_at: string | null
  provider: string | null
  provider_id: string | null
  booking_stats: {
    total: number
    paid: number
    unpaid: number
    cancelled: number
    room_bookings: number
    package_bookings: number
  }
  review_stats: {
    total: number
    avg_rating: number
    room_reviews: number
    package_reviews: number
  }
  support_stats: {
    total_tickets: number
  }
  transaction_stats: {
    total: number
    total_spent: number
    booking_transactions: number
  }
}

const Profile = () => {
  const { userDetails } = useUser()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    contact_number: '',
    address: '',
    city: '',
    state: '',
    country: ''
  })

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('firebase_auth_user')
    
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        if (userData && userData.email) {
          fetchProfile(userData.email)
          return
        }
      } catch (e) {
        console.error('Error parsing stored user:', e)
      }
    }
    
    if (userDetails && userDetails.email) {
      fetchProfile(userDetails.email)
      return
    }
    
    // Only redirect if we're sure there's no user
    const timeoutId = setTimeout(() => {
      const finalCheck = localStorage.getItem('firebase_auth_user') || (userDetails && userDetails.email)
      if (!finalCheck) {
        navigate('/auth/login', { replace: true })
      }
    }, 100)
    
    return () => clearTimeout(timeoutId)
  }, [userDetails, navigate])

  const fetchProfile = async (email: string) => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/customer/profile?email=${encodeURIComponent(email)}`
      )

      if (!response.ok) {
        if (response.status === 404) {
          // User doesn't exist in database, create profile from Firebase data
          setProfile({
            id: 0,
            first_name: userDetails?.displayName?.split(' ')[0] || null,
            last_name: userDetails?.displayName?.split(' ').slice(1).join(' ') || null,
            full_name: userDetails?.displayName || null,
            image: userDetails?.photoURL || null,
            username: null,
            email: email,
            email_verified: true,
            email_verified_at: new Date().toISOString(),
            contact_number: userDetails?.phoneNumber?.replace(/^\+91/, '') || null,
            address: null,
            city: null,
            state: null,
            country: null,
            status: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            provider: userDetails?.providerData?.[0]?.providerId || 'google',
            provider_id: userDetails?.uid || null,
            booking_stats: { total: 0, paid: 0, unpaid: 0, cancelled: 0 },
            review_stats: { total: 0, avg_rating: 0 }
          })
          setFormData({
            first_name: userDetails?.displayName?.split(' ')[0] || '',
            last_name: userDetails?.displayName?.split(' ').slice(1).join(' ') || '',
            contact_number: userDetails?.phoneNumber?.replace(/^\+91/, '') || '',
            address: '',
            city: '',
            state: '',
            country: ''
          })
          setLoading(false)
          return
        }
        throw new Error('Failed to fetch profile')
      }

      const data = await response.json()
      setProfile(data)
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        contact_number: data.contact_number || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || ''
      })
    } catch (err: any) {
      console.error('Error fetching profile:', err)
      setError(err.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (section: string) => {
    // Confirm before editing
    if (editing && editing !== section) {
      if (!confirm('You have unsaved changes. Do you want to discard them and edit a different section?')) {
        return
      }
    }
    setEditing(section)
  }

  const handleCancel = () => {
    // Confirm before canceling if there are changes
    const hasChanges = editing === 'personal' 
      ? (formData.first_name !== (profile?.first_name || '') || 
         formData.last_name !== (profile?.last_name || '') ||
         formData.contact_number !== (profile?.contact_number || ''))
      : (formData.address !== (profile?.address || '') ||
         formData.city !== (profile?.city || '') ||
         formData.state !== (profile?.state || '') ||
         formData.country !== (profile?.country || ''))
    
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return
      }
    }
    
    setEditing(null)
    // Reset form data to original profile data
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        contact_number: profile.contact_number || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        country: profile.country || ''
      })
    }
  }

  const handleSave = async (section: string) => {
    // Validate required fields
    if (section === 'personal') {
      if (!formData.first_name.trim() && !formData.last_name.trim()) {
        alert('Please enter at least first name or last name')
        return
      }
    }

    // Confirm before saving
    if (!confirm('Are you sure you want to save these changes?')) {
      return
    }

    try {
      setSaving(true)
      const email = userDetails?.email || JSON.parse(localStorage.getItem('firebase_auth_user') || '{}')?.email
      
      if (!email) {
        throw new Error('Email not found')
      }

      const updateData: any = { email: email }
      if (section === 'personal') {
        updateData.first_name = formData.first_name.trim() || null
        updateData.last_name = formData.last_name.trim() || null
        updateData.contact_number = formData.contact_number.trim() || null
      } else if (section === 'address') {
        updateData.address = formData.address.trim() || null
        updateData.city = formData.city.trim() || null
        updateData.state = formData.state.trim() || null
        updateData.country = formData.country.trim() || null
      }

      console.log('Updating profile with data:', updateData)

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/customer/profile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update profile' }))
        throw new Error(errorData.detail || 'Failed to update profile')
      }

      const updatedData = await response.json()
      console.log('Profile updated successfully:', updatedData)
      
      // Update profile with new data (backend now returns all stats)
      setProfile(updatedData)
      setEditing(null)
      
      // Show success message
      alert('Profile updated successfully!')
    } catch (err: any) {
      console.error('Error updating profile:', err)
      alert(err.message || 'Failed to update profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    )
  }

  if (error && !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => {
              const email = userDetails?.email || JSON.parse(localStorage.getItem('firebase_auth_user') || '{}')?.email
              if (email) fetchProfile(email)
            }}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!profile) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">My Profile</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your account information and preferences</p>
        </div>

        {/* Profile Overview Card */}
        <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6 mb-5 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="relative flex-shrink-0">
              {profile.image ? (
                <img
                  src={profile.image}
                  alt={profile.full_name || 'Profile'}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-primary-100"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary-100 flex items-center justify-center border-4 border-primary-200">
                  <User className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 truncate">
                {profile.full_name || profile.email}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mb-2 truncate">{profile.email}</p>
              <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  {profile.email_verified ? (
                    <>
                      <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                      <span className="text-green-600">Email Verified</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-500 flex-shrink-0" />
                      <span className="text-yellow-600">Email Not Verified</span>
                    </>
                  )}
                </div>
                {profile.provider && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Globe className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="capitalize">Signed in with {profile.provider}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-primary-500 flex-shrink-0" />
              <span className="text-xl sm:text-2xl font-bold text-gray-900">{profile.booking_stats.total}</span>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">Total Bookings</p>
            <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2 text-xs">
              <span className="text-green-600">{profile.booking_stats.paid} paid</span>
              <span className="text-gray-400">•</span>
              <span className="text-orange-600">{profile.booking_stats.unpaid} unpaid</span>
              {profile.booking_stats.cancelled > 0 && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-red-600">{profile.booking_stats.cancelled} cancelled</span>
                </>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {profile.booking_stats.room_bookings || 0} rooms, {profile.booking_stats.package_bookings || 0} packages
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <Star className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500 flex-shrink-0" />
              <span className="text-xl sm:text-2xl font-bold text-gray-900">
                {profile.review_stats.avg_rating > 0 ? profile.review_stats.avg_rating.toFixed(1) : '0.0'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">Average Rating</p>
            <p className="mt-1 text-xs text-gray-500">
              {profile.review_stats.total} reviews ({profile.review_stats.room_reviews || 0} rooms, {profile.review_stats.package_reviews || 0} packages)
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
              <span className="text-base sm:text-lg font-bold text-gray-900">
                ₹{profile.transaction_stats?.total_spent?.toFixed(2) || '0.00'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">Total Spent</p>
            <p className="mt-1 text-xs text-gray-500">
              {profile.transaction_stats?.total || 0} transactions
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-gray-900">
                {formatDate(profile.created_at)}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">Member Since</p>
            {profile.support_stats?.total_tickets > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {profile.support_stats.total_tickets} support tickets
              </p>
            )}
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6 mb-5 sm:mb-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500" />
              Personal Information
            </h3>
            {editing !== 'personal' ? (
              <button
                onClick={() => handleEdit('personal')}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('personal')}
                  disabled={saving}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="h-3 w-3 sm:h-4 sm:w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">First Name</label>
                {editing === 'personal' ? (
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                    placeholder="Enter first name"
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-900 py-2">{profile.first_name || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Last Name</label>
                {editing === 'personal' ? (
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                    placeholder="Enter last name"
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-900 py-2">{profile.last_name || 'Not set'}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2">
                <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                Email Address
              </label>
              <p className="text-sm sm:text-base text-gray-900 py-2">{profile.email}</p>
              <p className="text-xs text-gray-500 mt-0.5">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2">
                <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                Phone Number
              </label>
              {editing === 'personal' ? (
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 font-medium text-sm sm:text-base">+91</div>
                  <input
                    type="tel"
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="Enter 10-digit phone number"
                    maxLength={10}
                    className="w-full pl-11 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  />
                </div>
              ) : (
                <p className="text-sm sm:text-base text-gray-900 py-2">{profile.contact_number ? `+91${profile.contact_number}` : 'Not set'}</p>
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Username</label>
              <p className="text-sm sm:text-base text-gray-900 py-2">{profile.username || 'Not set'}</p>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6 mb-5 sm:mb-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500" />
              Address Information
            </h3>
            {editing !== 'address' ? (
              <button
                onClick={() => handleEdit('address')}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Edit2 className="h-3 w-3 sm:h-4 sm:w-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleSave('address')}
                  disabled={saving}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="h-3 w-3 sm:h-4 sm:w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Street Address</label>
              {editing === 'address' ? (
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                  placeholder="Enter your street address"
                />
              ) : (
                <p className="text-sm sm:text-base text-gray-900 py-2">{profile.address || 'Not set'}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">City</label>
                {editing === 'address' ? (
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                    placeholder="Enter city"
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-900 py-2">{profile.city || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">State</label>
                {editing === 'address' ? (
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                    placeholder="Enter state"
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-900 py-2">{profile.state || 'Not set'}</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">Country</label>
                {editing === 'address' ? (
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm sm:text-base"
                    placeholder="Enter country"
                  />
                ) : (
                  <p className="text-sm sm:text-base text-gray-900 py-2">{profile.country || 'Not set'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-5 sm:p-6">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary-500" />
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/bookings')}
              className="flex items-center gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
            >
              <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm sm:text-base text-gray-900">My Bookings</p>
                <p className="text-xs sm:text-sm text-gray-600">View and manage your bookings</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/rooms')}
              className="flex items-center gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
            >
              <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm sm:text-base text-gray-900">Browse Rooms</p>
                <p className="text-xs sm:text-sm text-gray-600">Explore available rooms</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile

